import React from 'react';
import styled from 'styled-components';
import Card from 'components/Card';
import { AttachmentUpload } from 'components/FormFields';
import CodeBlock from '../../components/CodeBlock';
import ComponentDemo from '../../components/ComponentDemo';

const PageHeader = styled.div`
  margin-bottom: 48px;
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 12px 0;
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin: 0;
  line-height: 1.6;
`;

const Section = styled.section`
  margin-bottom: 64px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 8px 0;
`;

const SectionDescription = styled.p`
  font-size: 15px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  line-height: 1.7;
  margin: 0 0 32px 0;
`;

const FileUploadDocs = () => {
  const [attachments, setAttachments] = React.useState([]);

  return (
    <div>
      <PageHeader>
        <Title>File Upload</Title>
        <Subtitle>
          AttachmentUpload component for uploading images and PDFs. Features
          file validation, upload progress, preview thumbnails, and collapsible
          UI. Located at <code>/components/FormFields/AttachmentUpload</code>
        </Subtitle>
      </PageHeader>

      <Section>
        <SectionTitle>Live Example</SectionTitle>
        <SectionDescription>
          Try uploading files to see the component in action. Supports images
          (JPG, PNG, GIF, SVG) and PDFs.
        </SectionDescription>

        <ComponentDemo label="AttachmentUpload Component" align="stretch">
          <div style={{ width: '500px', maxWidth: '100%' }}>
            <AttachmentUpload
              attachments={attachments}
              onAttachmentsChange={setAttachments}
              maxFiles={3}
              maxFileSize={2000000}
              channel="SMS"
            />
          </div>
        </ComponentDemo>
      </Section>

      <Section>
        <SectionTitle>Component Overview</SectionTitle>
        <SectionDescription>
          The AttachmentUpload component handles file uploads with built-in
          validation, GraphQL integration, and a polished UI for managing
          attachments.
        </SectionDescription>

        <Card title="Features" headerBorder>
          <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
            • <strong>File Types:</strong> Supports images (JPG, PNG, GIF, SVG)
            and PDFs
            <br />• <strong>Validation:</strong> File size and type validation
            with user-friendly error messages
            <br />• <strong>Error Handling:</strong> Uses Toaster component for
            error notifications (fallback available via onError prop)
            <br />• <strong>Upload Integration:</strong> GraphQL mutation for
            server upload
            <br />• <strong>Preview:</strong> Image thumbnails with file names
            and sizes
            <br />• <strong>Management:</strong> Add, remove, and view uploaded
            files
            <br />• <strong>Collapsible:</strong> Expandable/collapsible
            interface to save space
            <br />• <strong>Channel Aware:</strong> Shows appropriate limits
            based on channel (SMS vs Email)
            <br />• <strong>Error Handling:</strong> Callback support for custom
            error handling
          </div>
        </Card>
      </Section>

      <Section>
        <SectionTitle>Basic Usage</SectionTitle>
        <CodeBlock>
          {`import { AttachmentUpload } from 'components/FormFields';

const [attachments, setAttachments] = useState([]);

<AttachmentUpload
  attachments={attachments}
  onAttachmentsChange={setAttachments}
  maxFiles={3}
  maxFileSize={2000000} // 2MB
  channel="SMS"
/>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Props</SectionTitle>
        <Card headerBorder title="AttachmentUpload Props">
          <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
            • <strong>attachments</strong> (array): Current array of attachment
            objects
            <br />• <strong>onAttachmentsChange</strong> (function): Callback
            when attachments change
            <br />• <strong>maxFiles</strong> (number): Maximum number of files
            (default: 3)
            <br />• <strong>maxFileSize</strong> (number): Maximum file size in
            bytes (default: 2MB)
            <br />• <strong>channel</strong> (string): Communication channel -
            "SMS" or "Email"
            <br />• <strong>disabled</strong> (boolean): Whether upload is
            disabled
            <br />• <strong>onError</strong> (function): Optional custom error
            handler callback. If not provided, component uses built-in Toaster
            for errors
          </div>
        </Card>

        <Card
          title="Attachment Object Structure"
          headerBorder
          style={{ marginTop: '16px' }}
        >
          <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
            <strong>Each attachment object contains:</strong>
            <br />• <strong>id</strong> (string): Server-generated ID
            <br />• <strong>name</strong> (string): Original filename
            <br />• <strong>size</strong> (number): File size in bytes
            <br />• <strong>sizeFormatted</strong> (string): Human-readable file
            size (e.g., "1.2 MB")
            <br />• <strong>url</strong> (string): Server URL for the uploaded
            file
            <br />• <strong>type</strong> (string): MIME type
            <br />• <strong>isImage</strong> (boolean): Whether file is an image
            <br />• <strong>isPDF</strong> (boolean): Whether file is a PDF
            <br />• <strong>isSVG</strong> (boolean): Whether file is an SVG
          </div>
        </Card>
      </Section>

      <Section>
        <SectionTitle>Complete Example</SectionTitle>
        <CodeBlock>
          {`import React, { useState } from 'react';
import { AttachmentUpload } from 'components/FormFields';

const MessageForm = () => {
  const [attachments, setAttachments] = useState([]);

  // Optional: Custom error handler (component uses Toaster by default)
  const handleError = (error) => {
    console.log('Custom error handler:', error);
    // You can add custom logic here
  };

  const handleSend = () => {
    // attachments array contains uploaded file URLs
    const fileUrls = attachments.map(att => att.url);

    sendMessage({
      message: messageText,
      attachments: fileUrls,
    });
  };

  return (
    <div>
      <textarea
        value={messageText}
        onChange={(e) => setMessageText(e.target.value)}
      />

      {/* Component has built-in Toaster, onError is optional */}
      <AttachmentUpload
        attachments={attachments}
        onAttachmentsChange={setAttachments}
        maxFiles={3}
        maxFileSize={2000000}
        channel="SMS"
        // onError={handleError} // Optional custom error handler
      />

      <button onClick={handleSend}>
        Send Message
      </button>
    </div>
  );
};`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>File Size Limits</SectionTitle>
        <Card>
          <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
            <strong>Recommended limits by channel:</strong>
            <br />
            <br />• <strong>SMS:</strong> 2MB per file, max 3 files (carrier
            limitations)
            <br />• <strong>Email:</strong> 5MB per file, max 10 files
            <br />• <strong>Internal Messages:</strong> 10MB per file, max 5
            files
            <br />
            <br />
            The component automatically formats file sizes for display (KB, MB,
            GB).
          </div>
        </Card>
      </Section>

      <Section>
        <SectionTitle>Best Practices</SectionTitle>
        <ComponentDemo direction="column" gap="16px">
          <Card title="✅ Do" accentTitle>
            <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
              • Set appropriate maxFiles and maxFileSize based on channel
              <br />
              • Component uses built-in Toaster for errors by default
              <br />
              • Optionally provide onError callback for custom error handling
              <br />
              • Show upload progress/loading states
              <br />
              • Validate attachments array before sending messages
              <br />• Test with different file sizes and types (JPG, PNG, GIF,
              SVG, PDF)
            </div>
          </Card>

          <Card title="❌ Don't" accentTitle>
            <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
              • Don't forget to handle GraphQL upload errors
              <br />
              • Don't allow unlimited file sizes
              <br />
              • Don't submit without checking attachment upload completion
              <br />
              • Don't forget to clear attachments after successful send
              <br />• Don't forget SVG support when testing file uploads
            </div>
          </Card>
        </ComponentDemo>
      </Section>

      <Section>
        <SectionTitle>GraphQL Integration</SectionTitle>
        <SectionDescription>
          The component uses a GraphQL mutation for uploads. Ensure your schema
          supports file uploads.
        </SectionDescription>

        <CodeBlock>
          {`# GraphQL Schema
type Mutation {
  singleUpload(file: Upload!): UploadedFile!
}

type UploadedFile {
  id: ID!
  url: String!
}`}
        </CodeBlock>
      </Section>
    </div>
  );
};

export default FileUploadDocs;
